import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FaHeart, FaSave, FaCheck } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { API } from '../App';
import { Button } from './ui/button';

const INTEREST_OPTIONS = [
  { key: 'slushice', label: 'Slushice', emoji: '🧊' },
  { key: 'frappe', label: 'Kaffe / Frappé', emoji: '☕' },
  { key: 'milkshake', label: 'Mælk / Milkshake', emoji: '🥤' },
  { key: 'frozen_cocktails', label: 'Cocktail / Frozen Cocktails', emoji: '🍹' },
];

/**
 * Lets the user edit their interest tags after onboarding.
 * Reads the initial list from the user prop (auth context), persists via
 * PUT /api/user/interests.
 */
const InterestsSelector = ({ user }) => {
  const { t } = useTranslation();
  const [interests, setInterests] = useState(user?.interests || []);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setInterests(user?.interests || []);
  }, [user?.interests]);

  const toggle = (key) => {
    setInterests((prev) => (prev.includes(key) ? prev.filter((i) => i !== key) : [...prev, key]));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/user/interests`, { interests });
      toast.success(t('profile.interests.saved', 'Interesser gemt!'));
    } catch (err) {
      console.error('[InterestsSelector] save failed', err);
      toast.error(t('profile.interests.error', 'Kunne ikke gemme interesser'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-5 space-y-4">
      <div className="flex items-center gap-2">
        <FaHeart className="text-pink-500" />
        <h2 className="text-lg font-semibold">
          {t('profile.interests.title', 'Mine interesser')}
        </h2>
      </div>
      <p className="text-xs text-gray-500 -mt-2">
        {t('profile.interests.help', 'Vælg de kategorier du helst vil lave. Vi bruger dem til at vise relevante opskrifter.')}
      </p>

      <div className="grid grid-cols-2 gap-2.5">
        {INTEREST_OPTIONS.map((opt) => {
          const active = interests.includes(opt.key);
          return (
            <button
              key={opt.key}
              type="button"
              onClick={() => toggle(opt.key)}
              data-testid={`profile-interest-${opt.key}`}
              aria-pressed={active}
              className={`relative flex items-center gap-2 px-3 py-3 rounded-lg border-2 text-left transition-all ${
                active
                  ? 'border-blue-600 bg-blue-50 text-blue-900'
                  : 'border-gray-200 hover:border-gray-300 text-gray-800'
              }`}
            >
              <span className="text-xl">{opt.emoji}</span>
              <span className="text-sm font-semibold">{opt.label}</span>
              {active && (
                <span className="absolute top-1.5 right-1.5 inline-flex h-4 w-4 items-center justify-center rounded-full bg-blue-600 text-white text-[9px]">
                  <FaCheck />
                </span>
              )}
            </button>
          );
        })}
      </div>

      <div className="flex justify-end">
        <Button
          type="button"
          onClick={handleSave}
          disabled={saving}
          data-testid="profile-interests-save"
          className="bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2"
        >
          <FaSave />
          {saving ? t('common.saving', 'Gemmer...') : t('common.save', 'Gem')}
        </Button>
      </div>
    </div>
  );
};

export default InterestsSelector;
